import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AuthService } from '@mydocma/auth';
import { IdedMydocmaFile } from '@mydocma/common/src/lib/file';
import { Observable } from 'rxjs';
import { PR_CONFIG, PrConfig } from '../configuration/pr.config';
import { IDocumentExtendedRequest, IDocumentExtendedResponse } from '../models/document-extended';
import { IDocument, IFile } from '../models/documents';
import { IDownload, IDownloadSingleFileResponse } from '../models/document-dialog';

@Injectable({
  providedIn: 'root',
})
export class DocumentApiService {
  private readonly apiUrl: string;
  private jwtService: any;

  constructor(
    // @ts-ignore
    @Inject(PR_CONFIG) private config: PrConfig,
    private readonly httpClient: HttpClient,
    private readonly authService: AuthService,
  ) {
    this.apiUrl = this.config?.apiUrl + 'documents';
    this.jwtService = this.authService as any;
  }

  convertMsFilesToPDF(file: IFile): Observable<Blob> {
    const formData = new FormData();
    let nativeFile: File | Blob;
    if ((file as any).file instanceof File || (file as any).file instanceof Blob) {
      nativeFile = (file as any).file;
    } else if (file instanceof File || file instanceof Blob) {
      nativeFile = file;
    } else {
      throw new Error('File must be a native File or Blob for upload.');
    }
    formData.append('file', nativeFile, (nativeFile as File).name || file.name);
    return this.httpClient.post(`${this.apiUrl}/file/convertToPdf`, formData, {
      responseType: 'blob',
    });
  }

  getIds(filterOptions?: {}, onlyIds: boolean = true): Observable<string[]> {
    const params = { onlyIds: onlyIds };
    return this.httpClient.post<string[]>(`${this.apiUrl}/metadatas`, filterOptions, { params });
  }

  getDocumentsExtendedMetadata(
    request: IDocumentExtendedRequest,
  ): Observable<IDocumentExtendedResponse[]> {
    return this.httpClient.post<IDocumentExtendedResponse[]>(
      `${this.apiUrl}/metadatas/extended`,
      request,
    );
  }

  getDocumentsCount(filterOptions?: {}): Observable<number> {
    return this.httpClient.post<number>(`${this.apiUrl}/metadatas/count`, filterOptions);
  }

  getDocuments(metadataIds: string[], withDeletedFiles: boolean = false): Observable<IDocument[]> {
    const params = { withDeletedFiles: withDeletedFiles };
    return this.httpClient.post<IDocument[]>(`${this.apiUrl}/metadatasByIds`, metadataIds, {
      params,
    });
  }

  createDocument(
    metadata: IDocument,
    createDocumentPerFile: boolean = false,
  ): Observable<IDocument[]> {
    const formData = new FormData();
    formData.append('DocumentMetadata', JSON.stringify(metadata));
    return this.httpClient.post<IDocument[]>(`${this.apiUrl}`, formData, {
      params: { createDocumentPerFile },
    });
  }

  updateDocumentMetadata(document: Partial<IDocument> & { id: string }[]): Observable<IDocument[]> {
    const payload: Array<Partial<IDocument> & { id: string }> = document;

    return this.httpClient.patch<IDocument[]>(this.apiUrl, payload);
  }

  getVideoPreviewUrlByDocumentId(documentId: string, fileId: string): string {
    return `${
      this.apiUrl
    }/${documentId}/file/${fileId}?access_token=${this.jwtService.getAccessToken()}`;
  }

  uploadFileToDocumentByID(
    id: string,
    file: IdedMydocmaFile,
    fileRef: IFile = {},
  ): Observable<IDocument> {
    const formData = new FormData();

    let nativeFile: File | Blob;
    if ((file as any).file instanceof File || (file as any).file instanceof Blob) {
      nativeFile = (file as any).file;
    } else if (file instanceof File || file instanceof Blob) {
      nativeFile = file;
    } else {
      throw new Error('File must be a native File or Blob for upload.');
    }

    formData.append('file', nativeFile, (nativeFile as File).name || file.name);
    formData.append('FileRef', JSON.stringify(fileRef));

    return this.httpClient.post<IDocument>(`${this.apiUrl}/${id}/file`, formData);
  }

  getFileBlobByDocumentId(
    documentId: string,
    fileId: string,
    isDeleted: boolean = false,
    thumbnail: boolean = false,
    preview: boolean = false,
    currentLanguage: string = 'en',
  ): Observable<Blob> {
    return this.httpClient.get(`${this.apiUrl}/${documentId}/file/${fileId}`, {
      params: { isDeleted, thumbnail, preview, lang: currentLanguage },
      responseType: 'blob',
    });
  }

  getDocumentMetadataById(
    documentId: string,
    withDeletedFiles: boolean = false,
  ): Observable<IDocument> {
    const params = { withDeletedFiles: withDeletedFiles };
    return this.httpClient.get<IDocument>(`${this.apiUrl}/${documentId}`, { params });
  }

  deleteDocument(documentId: string[], skipFileDeleting: boolean = false): Observable<void> {
    const params = { skipFileDeleting: skipFileDeleting };
    return this.httpClient.post<void>(`${this.apiUrl}/deleteById`, documentId, { params });
  }

  downloadDocument(documentIds: string[], currentLanguage: string = 'en'): Observable<IDownload> {
    const now = new Date();
    const params = {
      clientTimestamp: now.getTime() - now.getTimezoneOffset() * 60000,
      lang: currentLanguage,
    };
    return this.httpClient.post<IDownload>(`${this.apiUrl}/downloadDocuments`, documentIds, {
      params,
    });
  }

  downloadFileByDocumentId(
    documentId: string,
    fileId: string,
    isDeleted?: boolean,
    preview?: boolean,
    lang?: string,
    asLink?: boolean,
  ): Observable<Blob | IDownloadSingleFileResponse> {
    const params = { isDeleted, thumbnail: false, preview, lang, asLink };
    const url = `${this.apiUrl}/${documentId}/file/${fileId}`;

    if (asLink) {
      return this.httpClient.get<IDownloadSingleFileResponse>(url, { params });
    }

    return this.httpClient.get<Blob>(url, {
      params,
    });
  }

  getDocumentMetadatabyID(
    documentId: string,
    withDeletedFiles: boolean = false,
  ): Observable<IDocument> {
    const params = { withDeletedFiles: withDeletedFiles };
    return this.httpClient.get<IDocument>(`${this.apiUrl}/${documentId}`, { params });
  }
}
